import {
  DeleteGlobalChatConversationAction,
  GLOBAL_CHAT_ACTION,
  ListGlobalChatConversationsAction,
  ListGlobalChatMessagesAction,
  AddGlobalChatMessageAction,
  ReplaceGlobalChatMessageAction,
  ReplaceGlobalChatConversationAction,
  SetGlobalChatConversationLoadingAction,
  UpsertGlobalChatConversationAction,
} from "./actions";
import { GlobalChatState } from "./types";

type GlobalChatActions =
  | ListGlobalChatConversationsAction
  | UpsertGlobalChatConversationAction
  | ReplaceGlobalChatConversationAction
  | SetGlobalChatConversationLoadingAction
  | DeleteGlobalChatConversationAction
  | ListGlobalChatMessagesAction
  | AddGlobalChatMessageAction
  | ReplaceGlobalChatMessageAction;

const sortConversationIds = (state: GlobalChatState) =>
  Object.values(state.conversations)
    .sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
    )
    .map((conversation) => conversation.id);

export const globalChatReducer = (
  state: GlobalChatState = {
    conversations: {},
    conversationOrder: [],
    messagesByConversationId: {},
    loadingByConversationId: {},
    hasLoadedConversations: false,
  },
  action: GlobalChatActions,
): GlobalChatState => {
  switch (action.type) {
    case GLOBAL_CHAT_ACTION.LIST_CONVERSATIONS: {
      const conversations = action.conversations.reduce<
        GlobalChatState["conversations"]
      >(
        (acc, conversation) => {
          acc[conversation.id] = conversation;
          return acc;
        },
        { ...state.conversations },
      );
      const nextState = {
        ...state,
        conversations,
        hasLoadedConversations: true,
      };
      return {
        ...nextState,
        conversationOrder: sortConversationIds(nextState),
      };
    }
    case GLOBAL_CHAT_ACTION.UPSERT_CONVERSATION: {
      const nextState = {
        ...state,
        conversations: {
          ...state.conversations,
          [action.conversation.id]: action.conversation,
        },
      };
      return {
        ...nextState,
        conversationOrder: sortConversationIds(nextState),
      };
    }
    case GLOBAL_CHAT_ACTION.REPLACE_CONVERSATION: {
      const conversations = { ...state.conversations };
      delete conversations[action.localConversationId];
      conversations[action.conversation.id] = action.conversation;

      const messagesByConversationId = {
        ...state.messagesByConversationId,
      };
      const localMessages =
        messagesByConversationId[action.localConversationId] ?? [];
      delete messagesByConversationId[action.localConversationId];
      messagesByConversationId[action.conversation.id] = [
        ...(messagesByConversationId[action.conversation.id] ?? []),
        ...localMessages.map((message) => ({
          ...message,
          conversation_id: action.conversation.id,
        })),
      ];

      const loadingByConversationId = {
        ...state.loadingByConversationId,
      };
      const isLoading = Boolean(
        loadingByConversationId[action.localConversationId],
      );
      delete loadingByConversationId[action.localConversationId];
      if (isLoading) {
        loadingByConversationId[action.conversation.id] = true;
      }

      const nextState = {
        ...state,
        conversations,
        messagesByConversationId,
        loadingByConversationId,
      };
      return {
        ...nextState,
        conversationOrder: sortConversationIds(nextState),
      };
    }
    case GLOBAL_CHAT_ACTION.SET_CONVERSATION_LOADING: {
      const loadingByConversationId = {
        ...state.loadingByConversationId,
      };
      if (action.isLoading) {
        loadingByConversationId[action.conversationId] = true;
      } else {
        delete loadingByConversationId[action.conversationId];
      }
      return {
        ...state,
        loadingByConversationId,
      };
    }
    case GLOBAL_CHAT_ACTION.DELETE_CONVERSATION: {
      const conversations = { ...state.conversations };
      const messagesByConversationId = { ...state.messagesByConversationId };
      const loadingByConversationId = { ...state.loadingByConversationId };
      delete conversations[action.conversationId];
      delete messagesByConversationId[action.conversationId];
      delete loadingByConversationId[action.conversationId];

      const nextState = {
        ...state,
        conversations,
        messagesByConversationId,
        loadingByConversationId,
      };
      return {
        ...nextState,
        conversationOrder: sortConversationIds(nextState),
      };
    }
    case GLOBAL_CHAT_ACTION.LIST_MESSAGES:
      return {
        ...state,
        messagesByConversationId: {
          ...state.messagesByConversationId,
          [action.conversationId]: action.messages,
        },
      };
    case GLOBAL_CHAT_ACTION.ADD_MESSAGE: {
      const messages =
        state.messagesByConversationId[action.conversationId] ?? [];
      return {
        ...state,
        messagesByConversationId: {
          ...state.messagesByConversationId,
          [action.conversationId]: [...messages, action.message],
        },
      };
    }
    case GLOBAL_CHAT_ACTION.REPLACE_MESSAGE: {
      const messages =
        state.messagesByConversationId[action.conversationId] ?? [];
      return {
        ...state,
        messagesByConversationId: {
          ...state.messagesByConversationId,
          [action.conversationId]: messages.map((message) =>
            message.id === action.tempId ? action.message : message,
          ),
        },
      };
    }
    default:
      return state;
  }
};
